 
import { useRef, useEffect, useState, useCallback, Fragment } from 'react'
import { useId } from 'react'
import { useDarkMode } from 'usehooks-ts'

import Link from '@/components/link-component'
import SearchNavIcon from '@/components/search/nav-icon'
import NavContainer from '@/components/nav-container'
import HeaderDropdownMenu from '@/components/header-dropdown-menu'
import HeaderRichMenu, { HeaderRichMenuContent } from '@/components/header-rich-menu'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import StaggerList, { StaggerListItem } from '@/components/ui/stagger-list'
import HeaderBanner from '@/components/header-banner'
import Disclosure from '@/components/ui/disclosure'
import DynamicAction from '@/components/ui/dynamic-action'
import { Experience } from '@ninetailed/experience.js-react'
import { mapNtExperiences } from '@/utils/ninetailed'
import routes from '@/constants/routes'

import Hamburger from '@/icons/bwi/hamburger.svg'
import Close from '@/icons/bwi/close.svg'
import LogoHorizontal from '@/icons/logo-horizontal.svg'
import ArrowDown from '@/icons/bwi/angle-down.svg'

import { useHasScrolled } from '@/utils/dom'
import colors from '@/constants/colors'
import { AnyLinkType, flattenLinkGroups } from '@/utils/header'
import { cx } from '@/utils/strings.ts'
import { isTypeComponentCtaLink, isTypeHeaderDropdownMenu, isTypeHeaderRichMenu } from '@/types/ctf'

function MobileDisclosureArrow(isOpen: boolean) {
  return (
    <span
      className={cx(
        `w-4 h-4 [&_svg]:text-indigoBlue [&_svg]:dark:text-white transition-transform`,
        isOpen && `rotate-180`
      )}
    >
      <ArrowDown />
    </span>
  )
}

type HeaderComponentProps = {
  backgroundColor?: string
  ctaNavItems: AnyLinkType[]
  mainNavItems: AnyLinkType[]
  showSearch: boolean
  headerBanners: any
  newsletterForm: any
}

export function HeaderComponent({
  backgroundColor = colors.white,
  ctaNavItems,
  mainNavItems,
  showSearch,
  headerBanners,
  newsletterForm,
}: HeaderComponentProps) {
  // Primary blue is retired, so we use white as the default background color
  // TODO: Migrate all instances of primary blue to white in contentful after new design launch
  if (backgroundColor === colors.primaryBlue) {
    backgroundColor = colors.white
  }

  const mainNavLinks = flattenLinkGroups(mainNavItems)
  const ctaNavLinks = flattenLinkGroups(ctaNavItems)

  const updateMobileDownloadLink = (ctaLink: any) => {
    if (ctaLink?.link?.url?.endsWith('/download/')) {
      return {
        ...ctaLink,
        link: {
          ...ctaLink,
          url: `${ctaLink.link.url}#downloads-mobile`,
        },
      }
    }
    return ctaLink
  }
  function transformCustomMobileLinks<T extends AnyLinkType>(link: T): T {
    if (!link) return link
    if ('sys' in link) {
      if (isTypeComponentCtaLink(link)) {
        return updateMobileDownloadLink(link)
      }
      if (isTypeHeaderDropdownMenu(link)) {
        return {
          ...link,
          links: link.fields.links?.map((nestedLink) => updateMobileDownloadLink(nestedLink)),
        }
      }
      if (isTypeHeaderRichMenu(link)) {
        return {
          ...link,
          cards: link.fields.cards?.map((card) => ({
            ...card,
            links: card?.fields.links?.map((nestedLink) => ({
              ...nestedLink,
              link: updateMobileDownloadLink(nestedLink?.fields.link),
            })),
          })),
          links: link.fields.links,
        }
      }
    }

    return link
  }

  const AreMainNavItemsEmpty = (mainNavItems?.length || 0) === 0
  const mobileMainNavLinks = flattenLinkGroups(!AreMainNavItemsEmpty ? mainNavItems : ctaNavItems).map(
    transformCustomMobileLinks
  )

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const closeDrawer = () => setIsDrawerOpen(false)
  const [topRef, hasScrolled] = useHasScrolled()
  const headerRef = useRef<HTMLElement>(null)

  let ctaBtnSolidCount = 0
  let ctaBtnSolidCountMobile = 0

  const { isDarkMode } = useDarkMode()

  return (
    <>
      <div ref={topRef as never} className="h-0" />
      <HeaderBanner banners={headerBanners} />
      <header
        className={cx(
          `sticky top-0 left-0 z-50 transition duration-300`,
          hasScrolled && `shadow-lg`,
          backgroundColor === colors.white && `bg-white text-primaryBlue border-b border-secondary-100`,
          backgroundColor === colors.primaryBlue &&
            `bg-primaryBlue text-white [*>a]:text-white border-b border-primary-700`,
          'dark:bg-secondary-900 dark:border-secondary-700',
          isDrawerOpen && `z-10000`
        )}
        ref={headerRef}
      >
        {/* Mobile Nav */}
        <nav
          data-testid="bw-mobile-header"
          className="relative max-w-7xl mx-auto px-5 flex justify-between items-center py-4 lg:hidden"
        >
          <div className="w-32">
            <Link to={routes.index} title="Bitwarden homepage">
              <LogoHorizontal
                className={cx(
                  backgroundColor === colors.primaryBlue && `text-white`,
                  backgroundColor === colors.white && `text-primaryBlue dark:text-white`
                )}
              />
            </Link>
          </div>
          <div className="flex gap-3 items-center">
            {showSearch && <SearchNavIcon />}
            {mobileMainNavLinks.length + ctaNavLinks.length > 0 && (
              <button
                type="button"
                className={cx(
                  'w-7 h-7 flex cursor-pointer transition-colors duration-200 hover:text-secondary-200 dark:text-white',
                  isDrawerOpen && `pointer-events-none`
                )}
                data-name="bwi-hamburger"
                onClick={() => {
                  setIsDrawerOpen(!isDrawerOpen)
                }}
              >
                {/* we really should make sure our icons are uniform in size / canvas area */}
                {!isDrawerOpen ? <Hamburger className="w-12" /> : <Close className="m-auto w-3" />}
              </button>
            )}
          </div>

          {/* Mobile Dropdown */}
          <NavContainer
            backgroundColor="bg-white dark:bg-secondary-900"
            position="right"
            isDrawerOpen={isDrawerOpen}
            drawerBreakpoint="lg"
            className="p-6 pb-16"
            onRequestClose={closeDrawer}
            headerRef={headerRef}
            showOverlay={false}
            fullWidth
          >
            <div style={{ minHeight: `calc(100vh - var(--nav-container-drawer-offset)` }} className="flex flex-col ">
              <StaggerList
                className={`flex-1 list-none pb-[var(--nav-container-drawer-offset)]`}
                isVisible={isDrawerOpen}
              >
                {mobileMainNavLinks.map((link) => {
                  if (!link) return null
                  if (isTypeComponentCtaLink(link)) {
                    return (
                      <StaggerListItem key={link.sys.id} className="not-last:border-b-[1px] border-secondary-200">
                        <DynamicAction
                          className={cx('block px-6 py-3 text-xl text-indigoBlue dark:text-white ')}
                          onClick={closeDrawer}
                          {...(link.fields as any)}
                          id={link.sys.id}
                          nonCtaLink
                        />
                      </StaggerListItem>
                    )
                  }
                  if (isTypeHeaderDropdownMenu(link)) {
                    return (
                      <StaggerListItem
                        key={link.sys.id}
                        className={cx(
                          `p-0 not-last:border-b-[1px] border-b-secondary-200 [&_button]:px-6 [&_button]:py-3 [&_button]:w-full [&_button]:rounded-none [&_button]:bg-secondary-50 [&_button]:items-center [&_button]:justify-between`
                        )}
                      >
                        <Disclosure
                          className="bg-transparent! dark:hover:bg-transparent"
                          heading={
                            <div className="flex items-center justify-between text-xl text-indigoBlue dark:text-white">
                              <>{link.fields.title}</>
                            </div>
                          }
                          ArrowIcon={MobileDisclosureArrow}
                        >
                          <ul className="flex flex-col">
                            <>
                              {link?.fields?.links?.map((nestedLink) => (
                                <li className="list-none" key={nestedLink?.sys.id}>
                                  <DynamicAction
                                    className="block py-3 pl-10 text-lg text-indigoBlue dark:text-white border-t-[1px] border-secondary-100"
                                    onClick={closeDrawer}
                                    {...(nestedLink?.fields as any)}
                                    id={link.sys.id}
                                    nonCtaLink
                                  />
                                </li>
                              ))}
                            </>
                          </ul>
                        </Disclosure>
                      </StaggerListItem>
                    )
                  }
                  if (isTypeHeaderRichMenu(link)) {
                    return (
                      <StaggerListItem
                        key={link.sys.id}
                        className={cx(
                          `p-0 not-last:border-b-[1px] border-b-secondary-200 [&_button]:px-6 [&_button]:py-3 [&_button]:w-full [&_button]:rounded-none [&_button]:bg-secondary-50 [&_button]:items-center [&_button]:justify-between`
                        )}
                      >
                        <Disclosure
                          className="bg-transparent! dark:hover:bg-transparent"
                          heading={
                            <div className="flex items-center justify-between text-xl text-indigoBlue dark:text-white">
                              {link.fields.title}
                            </div>
                          }
                          ArrowIcon={MobileDisclosureArrow}
                        >
                          <div className="px-2 pb-6">
                            <HeaderRichMenuContent
                              links={link.fields.links}
                              cards={link.fields.cards}
                              includeNewsletter={false}
                              isCondensed
                              onLinkClick={closeDrawer}
                            />
                          </div>
                        </Disclosure>
                      </StaggerListItem>
                    )
                  }
                  return null
                })}
                {showSearch && (
                  <StaggerListItem className="px-4 py-6 not-last:border-b-[1px] border-secondary-200">
                    <SearchNavIcon>
                      <span className="text-lg ml-2">Search</span>
                    </SearchNavIcon>
                  </StaggerListItem>
                )}
              </StaggerList>
              {ctaNavLinks?.length > 0 && !AreMainNavItemsEmpty && (
                <div className="sticky bottom-[var(--nav-container-drawer-offset)] translate-y-[62px] left-0 w-full bg-white dark:bg-secondary-900 flex flex-row flex-wrap items-center justify-between py-6">
                  {ctaNavLinks.map((ctaLink) => {
                    if (isTypeComponentCtaLink(ctaLink)) {
                      if ((ctaLink as any)?.fields.style === buttonStyles.btnSolid) {
                        ctaBtnSolidCountMobile += 1
                      }
                      return (
                        <DynamicAction
                          key={ctaLink.sys.id}
                          onClick={closeDrawer}
                          {...(ctaLink.fields as any)}
                          size="Smaller"
                          className={cx('no-underline!')}
                        />
                      )
                    }
                    return null
                  })}
                </div>
              )}
            </div>
          </NavContainer>
        </nav>

        {/* Desktop Nav */}
        <nav
          data-testid="bw-desktop-header"
          className="relative hidden max-w-8xl mx-auto py-[1.125rem] px-6 xl:px-9 justify-between gap-4 lg:flex"
        >
          <div className="flex items-center">
            <div className="w-36 2xl:w-44 py-3 mr-5 xl:mr-10 2xl:mr-12">
              <Link to={routes.index}>
                <LogoHorizontal
                  className={cx(
                    backgroundColor === colors.primaryBlue || backgroundColor === colors.indigoBlue
                      ? 'text-white'
                      : 'text-primaryBlue',

                    'dark:text-white'
                  )}
                  title="Bitwarden homepage"
                />
              </Link>
            </div>
            <div className="mr-3 2xl:mr-5">
              {mainNavLinks?.length > 0 && (
                <ul className="flex gap-4 xl:gap-6 items-center list-none text-base 2xl:text-lg">
                  {mainNavLinks.map((link) => {
                    const linkFields = { ...link.fields, style: buttonStyles.btnPlain }
                    if (isTypeComponentCtaLink(link)) {
                      return (
                        <li key={link.sys.id} className="leading-5">
                          <DynamicAction
                            className={cx(
                              'text-base py-3 no-underline border-0 p-0 transition-colors duration-200',
                              backgroundColor === colors.primaryBlue &&
                                `text-white hover:text-secondary-200 dark:hover:text-primaryBlue`,
                              backgroundColor === colors.white &&
                                `hover:text-indigoBlue dark:text-white dark:hover:text-primaryBlue`
                            )}
                            {...linkFields}
                            nonCtaLink
                          />
                        </li>
                      )
                    }
                    if (isTypeHeaderDropdownMenu(link)) {
                      return (
                        <li key={link.sys.id} className="leading-5">
                          <HeaderDropdownMenu {...link.fields} headerBackgroundColor={backgroundColor} />
                        </li>
                      )
                    }
                    if (isTypeHeaderRichMenu(link)) {
                      return (
                        <li key={link.sys.id} className="leading-5">
                          <HeaderRichMenu
                            {...link.fields}
                            headerBackgroundColor={backgroundColor}
                            newsletterForm={newsletterForm}
                          />
                        </li>
                      )
                    }
                    return null
                  })}
                </ul>
              )}
            </div>
            {showSearch && <SearchNavIcon backgroundColor={backgroundColor} />}
          </div>
          <div className="flex gap-4 2xl:gap-5 items-center">
            {ctaNavLinks.map((ctaLink) => {
              if (isTypeComponentCtaLink(ctaLink)) {
                const isBtnSolid = ctaLink.fields.style === buttonStyles.btnSolid
                if (isBtnSolid) {
                  ctaBtnSolidCount += 1
                }

                const isAltColor = ctaBtnSolidCount % 2 === 0
                const style = isAltColor ? buttonStyles.btnOutlined : ctaLink.fields.style
                const uid = useId() + ctaLink.sys.id
                const sharedProps = {
                  onClick: () => {
                    ;(ctaLink.fields as any).onClick?.()
                  },
                  to: ctaLink.fields.link?.fields.url,
                  id: uid,
                  'data-testid': 'cta-link',
                  plausibleEventName: ctaLink.fields.plausibleEventName,
                }

                return (
                  <Fragment key={uid}>
                    {/* Desktop */}
                    <CtaLink
                      {...sharedProps}
                      style={style}
                      className={cx(
                        'hidden! xl:inline-block!',
                        (style === buttonStyles.btnPlain || style === buttonStyles.btnText) &&
                          'no-underline! hover:underline! visited:underline! px-4 py-3'
                      )}
                    >
                      {ctaLink.fields.label}
                    </CtaLink>
                    {/* Mobile */}
                    <CtaLink
                      {...sharedProps}
                      style={buttonStyles.btnText}
                      className={cx('xl:hidden! no-underline! hover:underline! px-2 py-1')}
                    >
                      {ctaLink.fields.label}
                    </CtaLink>
                  </Fragment>
                )
              }
              return null
            })}
          </div>
        </nav>
      </header>
    </>
  )
}

type HeaderProps = HeaderComponentProps & {
  id: string
  nt_experiences: any
}

export default function Header(props: HeaderProps): JSX.Element {
  const Loader = useCallback(
    () => (
      <div className="bg-primaryBlue opacity-95 sticky top-0 left-0 z-50 ">
        <div className="animate-pulse bg-primaryBlue w-full h-[72px] lg:h-[64px] xl:h-[68px] relative hidden max-w-8xl mx-auto px-9 justify-between gap-4 lg:flex">
          <div className="flex items-center">
            <div className="w-36 2xl:w-44 py-3 mr-6 xl:mr-10 2xl:mr-12">
              <LogoHorizontal className="text-white" />
            </div>
          </div>
        </div>
      </div>
    ),
    []
  )

  return (
    <Experience
      {...props}
      id={props.id}
      experiences={mapNtExperiences(props.nt_experiences)}
      component={HeaderComponent}
      loadingComponent={Loader}
      passthroughProps={{ headerBanners: props.headerBanners, newsletterForm: props.newsletterForm }}
    />
  )
}
